import { format } from 'date-fns';
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';

import ConmebolWarningModal from '../../components/ConmebolWarningModal';
import ErrorMessage from '../../components/ErrorModal';
import PrivacyBotafogoModal from '../../components/PrivacyBotafogoModal';
import TermModal from '../../components/TermModal';
import { Categories } from '../../constants/categories';
import { CookiesKeys } from '../../constants/cookiesKeys';
import { Environment } from '../../constants/environments';
import { EventTrackLabel } from '../../constants/ga4';
import { HttpVerb } from '../../constants/httpVerbs';
import { Producers } from '../../constants/producers';
import { DictonaryKeys } from '../../enums/noTickets';
import FacebookPixel from '../../trackings/FacebookPixel';
import GoogleAnalytics from '../../trackings/GoogleAnalytics';
import { AssignmentTickets } from '../../types/cart';
import { CouponDetail, CouponObject } from '../../types/cupom';
import { Dependent } from '../../types/dependent';
import { Event } from '../../types/event';
import { responseEventType, EventType } from '../../types/eventType';
import {
  Membership,
  MembershipRuleValue,
} from '../../types/membership';
import { Combo, ResponseComboList, ResponseTicket, Ticket } from '../../types/ticket';
import { EventData, ResponseCheckWallet } from '../../types/wallet';
import { captureFbclid } from '../../utils/fbclid';
import getCookieValue from '../../utils/getCookieValue';
import getIngCookieValue from '../../utils/getIngCookies';
import { logger } from '../../utils/logger';
import { normalizeDocument } from '../../utils/normalizeMembership';
import { associatedFaceRecognitionInThisSession } from '../../utils/wallet';
import { useApiService } from '../useApiService';
import useFormattedDateTimeZone from '../useFormattedDateTimeZone';
import { useMetaConversions } from '../useMetaConversions';
import useRules from '../useRules';
import { hasTermModalContent } from './helpers/eventTerms';
import {
  anyGroupHasFaceRecognition,
  shouldFetchFacialRecognitionUrl,
} from './helpers/faceRecognition';
import { formatTickets as formatTicketsHelper } from './helpers/formatTickets';
import { useI18nContext } from './i18n.context';
import { useLoaderScreenContext } from './loader-screen.context';
import { useMembershipContext } from './membership.context';
import { useUserContext } from './user.context';

export type EventContextProps = {
  comboList: Combo[];
  producerId: number;
  sessions: SessionsType[];
  event: Event | null;
  tickets: Ticket[];
  loading: boolean;
  isFootball: boolean;
  isBotafogo: boolean;
  hasTermModal: boolean;
  singleDayDate: string;
  currencyFormat: string;
  passportsDays: string | null;
  dataCarousel: DataCarouselProps[];
  selectedIdDateCarousel: number;
  setSelectedIdDateCarousel: (id: number) => void;
  allTicketsUnavailable: boolean;
  couponApplied: CouponDetail | null;
  passKeyApplied: string | null;
  listOfDependents: Dependent[];
  startImage: { large: string; small: string } | null;
  someGroupRequiresRF: boolean;
  showQuantityInStock: boolean;
  errorsLimit: { id: number, error: string }[];
  isOGlobo: boolean;
  signatureOGloboApplied: string | null;
  handleSetErrorsInSummary: (errors: { id: number, error: string }[]) => void;
  getTicketsInMyWallet: (userId: number, namingData: AssignmentTickets[]) => Promise<boolean>;
  updateDependentList: ({ dependents }: { dependents: Dependent[] }) => void;
  updateTicketList: (newTickets: Ticket[], event: Event) => void;
  getGetTickets: ({ event }: { event: Event }) => Promise<void>;
  openModalBotafogo: ({
    setContentModal,
    toggleModal,
  }: {
    setContentModal: (content: React.ReactNode) => void
    toggleModal: ({ open }: { open: boolean }) => void,
  }) => void;
  openTermModal: ({
    setContentModal,
    toggleModal,
  }: {
    setContentModal: (content: React.ReactNode) => void
    toggleModal: ({ open }: { open: boolean }) => void,
  }) => void;
  applyRulesAfterAddItemInCart: ({
    cartItems,
  }: {
    cartItems: Ticket[];
  }) => void;
  applyPassKey: (props: {
    passkey: string,
    modalIsOpen: boolean,
    setContentModal: (content: React.ReactNode) => void,
    toggleModal: ({ open }: { open: boolean }) => void
  }) => Promise<boolean>;
  applyCoupon: (props: {
    coupon: string,
    modalIsOpen: boolean,
    setContentModal: (content: React.ReactNode) => void,
    toggleModal: ({ open }: { open: boolean }) => void
  }) => Promise<boolean>;
  applyGloboSignature: (props: {
    globoSignature: string,
    modalIsOpen: boolean,
    setContentModal: (content: React.ReactNode) => void,
    toggleModal: ({ open }: { open: boolean }) => void
  }) => Promise<boolean>;
  selectIdDateCarousel: (id: number) => void;
  removeCouponApplied: () => Promise<void>;
  removePassKeyApplied: () => Promise<void>;
  removeSignatureOGloboApplied: () => Promise<void>;
  ticketGroupSelected: Ticket | null;
  selectTicketGroup: (ticket: Ticket | null) => void;
  ticketsForThisEventInMyWallet: EventData[];
  changeEventLoading: (active: boolean) => void;
  updateTicketsWithMembership: (
    membership: Membership,
    passkey: string,
    document: string,
    toggleModal: ({ open }: {open: boolean}) => void,
    setContentModal: (content: React.ReactNode) => void
  ) => Promise<boolean>;
  noTicketContentKey: DictonaryKeys;
  showNoTicketsContent: boolean;
};

export type TicketProps = {
  ticket: Ticket;
};

export type FixedParams = {
  clientUrl: string;
  eventId: string;
};

export type SessionsType = {
  dateTime: string;
  id: number;
  status: string;
};

export type DataCarouselProps = {
  id: number;
  title: string;
  subtitle?: string;
  sessionTitle?: string;
  status?: string | string[];
  isPassport: boolean;
  days: string | null;
  weekAndHour: string | null;
  hasTicketAvailable: boolean;
  hasResale?: boolean;
  resaleCount?: number;
  groupsReference: number[];
  definitiveSessionId: string;
  finished: boolean;
  tickets?: Ticket[];
  callback: () => void;
};

const DEFAULT_PRODUCER_ID = 1;

const EventContext = createContext({} as EventContextProps);

function EventProvider({ children }: { children: ReactNode }) {
  const [startImage, setStartImage] = useState<{
    large: string;
    small: string;
  } | null>(null);
  const [producerId, setProducerId] = useState(DEFAULT_PRODUCER_ID);
  const [errorsLimit, setErrorsLimit] = useState<{ id: number, error: string }[]>([]);
  const [showQuantityInStock, setShowQuantityInStock] = useState(false);
  const [ticketsForThisEventInMyWallet, setTicketsForThisEventInMyWallet] = useState<EventData[]>([]);
  const [dataCarousel, setDataCarousel] = useState<DataCarouselProps[]>([]);
  const [passportsDays, setPassportsDays] = useState<string | null>(null);
  const [selectedIdDateCarousel, setSelectedIdDateCarousel] = useState(0);
  const [allTicketsUnavailable, setAllTicketsUnavailable] = useState(true);
  const [event, setEvent] = useState<Event | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [sessionDetails, setSessionDetails] = useState<EventType | null>(null);
  const [currencyFormat, setCurrencyFormat] = useState('BRL');
  const [loading, setLoading] = useState(true);
  const [singleDayDate, setSingleDayDate] = useState('');
  const [couponApplied, setCouponApplied] = useState<CouponDetail | null>(null);
  const [passKeyApplied, setPassKeyApplied] = useState<string | null>(null);
  const [isFootball, setIsFootball] = useState(false);
  const [isBotafogo, setIsBotafogo] = useState(false);
  const [hasTermModal, setHasTermModal] = useState(false);
  const [sessions, setSessions] = useState<SessionsType[]>([]);
  const [listOfDependents, setListOfDependents] = useState<Dependent[]>([]);
  const [someGroupRequiresRF, setSomeGroupRequiresRF] = useState(false);
  const [ticketGroupSelected, setTicketGroupSelected] = useState<Ticket | null>(
    null
  );
  const [isOGlobo, setIsOGlobo] = useState(false);
  const [signatureOGloboApplied, setSignatureOGloboApplied] = useState<string | null>(null);
  const [comboList, setComboList] = useState<Combo[]>([]);
  const [noTicketContentKey, setNoTicketContentKey] = useState<DictonaryKeys>(DictonaryKeys.soldOut);
  const [showNoTicketsContent, setShowNoTicketsContent] = useState(false);
  const [resaleDateIds, setResaleDateIds] = useState<Map<number, { count: number; datetime: string }>>(new Map());

  const { formatDate } = useFormattedDateTimeZone();

  const {
    currentLocale : { locale },
    translate,
    supportedLocales,
  } = useI18nContext();
  const { user, getFacialRecognitionUrl, authenticatedUser } = useUserContext();
  const { request } = useApiService();
  const { toggleLoader } = useLoaderScreenContext();

  const {
    applyTicketRules,
    checkIfAllTicketsAreUnavailable,
    checksIfAllTicketsAreSoldOut,
    hasSellableTicketType,
    bffAddExtraKeysToTicketListing,
    applyCoupon : applyCouponRules,
  } = useRules();

  const { eventId } = useParams<FixedParams>();
  const {
    verifyThisMembershipHasTicket,
    changeMembershipApplied,
    handleMembershipLoadings,
    handleMembershipError,
  } = useMembershipContext();

  const updateDependentList = async ({
    dependents,
  }: {
    dependents: Dependent[];
  }) => {
    setListOfDependents(dependents);
  };

  const getTicketsInMyWallet = async (userId: number, namingData: AssignmentTickets[]) => {
    const userToken = getIngCookieValue('token')!;
    const apiKey = Environment.API_KEY;
    try {
      const { responseData : { data } } = await request<null, { responseData: ResponseCheckWallet }>(
        {
          requestOptions : {
            resourcePath : `user/${userId}/tickets?apikey=${apiKey}&usertoken=${userToken}&eventId=${eventId}`
          },
          type : HttpVerb.GET,
        },
        { baseUrl : Environment.API_URL, needToken : true }
      );

      // TICKET_SELECTION_ERROR_TITLE

      const invalidTickets = namingData.filter((curTicket) => (
        !curTicket.isTransferableGuestType && associatedFaceRecognitionInThisSession(
          data,
          curTicket,
        )
      ));

      const formattedInvalidTicketsForErrorModal = invalidTickets.map((curTicket) => ({
        id : curTicket.id,
        error : translate(['INFORMATION', 'ASSOCIATION_ERROR_FACE_RECOGNITION_SELECTION']),
      }));

      setErrorsLimit(formattedInvalidTicketsForErrorModal);
      const isValidAssociations = formattedInvalidTicketsForErrorModal.length === 0;

      setTicketsForThisEventInMyWallet(data);
      return isValidAssociations;
    } catch (error) {
      logger.error(error as string);
      return false;
    }
  };

  const handleSetErrorsInSummary = (errors: { id: number, error: string }[]) => {
    setErrorsLimit(errors);
  };

  /**
   * Retrieves the list of dependents for the current user and event.
   * This function is only called if the event is of type football and is automatically triggered when the user logs in.
   * @async
   * @function getDependentList
   * @returns Promise<void> A promise that resolves to the list of dependents.
   * If the conditions are not met, it returns an empty array.
   */
  const getDependentList = async () => {
    const apiKey = Environment.API_KEY;
    const userToken = getIngCookieValue('token')!;
    const document = user!.document.number.replace(/[.-]/g, '');
    try {
      const { detail : dependents } = await request<
        null,
        { detail: Dependent[] }
      >(
        {
          requestOptions : {
            resourcePath :
              `event/${eventId}/user/${user!.id}/dependents?${apiKey}` +
              `&usertoken=${userToken}&document=${document}&producerId=${producerId}`,
          },
          type : HttpVerb.GET,
        },
        { baseUrl : Environment.API_TICKETS_URL, needToken : true }
      );

      setListOfDependents(dependents.map((dependent, index) => ({ ...dependent, hasTicket : index === 0 })));
    } catch (error) {
      logger.error(error as string);
    }
  };
  const openModalBotafogo = ({
    toggleModal,
    setContentModal
  }: {
    toggleModal: ({ open }: { open: boolean }) => void,
    setContentModal: (content: React.ReactNode) => void
  }) => {
    setContentModal(<PrivacyBotafogoModal />);
    toggleModal({ open : true });
  };

  const openTermModal = ({
    toggleModal,
    setContentModal
  }: {
    toggleModal: ({ open }: { open: boolean }) => void,
    setContentModal: (content: React.ReactNode) => void
  }) => {
    setContentModal(<TermModal />);
    toggleModal({ open : true });
  };

  /**
   * Updates the list of tickets with new tickets.
   *
   * @param {Ticket[]} newTickets - The new list of tickets to be set.
   */
  const updateTicketList = (newTickets: Ticket[], event: Event) => {
    let filteredTickets = newTickets.filter((ticketGroup) => (
      ticketGroup.status === 'available' && ticketGroup.salable ||
      ticketGroup.status === 'soldout' && event?.attributes.showSouldOut.value
    ));

    filteredTickets = filteredTickets.map((ticketGroup) => ({
      ...ticketGroup,
      type : ticketGroup.type.filter((ticket) => ((
        ticket.status === 'available'
      ) || (
        ticket.status === 'soldout' && event?.attributes.showSouldOut.value
      ))),
    }));

    filteredTickets = filteredTickets.filter((ticketGroup) => ticketGroup.type.length > 0);
    setTickets(filteredTickets);

    if (ticketGroupSelected) {
      const updatedTicketGroupSelected = filteredTickets.find((ticket) => ticket.id === ticketGroupSelected?.id);
      setTicketGroupSelected(updatedTicketGroupSelected || null);
    }

    if (passKeyApplied)
      setPositionCarouselIfCouponOrPasskeyExists({ tickets : filteredTickets });
  };

  const updateTicketsWithMembership = async (
    membership: Membership,
    passkey: string,
    document: string,
    toggleModal: ({ open }: {open: boolean}) => void,
    setContentModal: (content: React.ReactNode) => void
  ) => {
    const normalizedDocument = normalizeDocument(document);
    const apiKey = Environment.API_KEY;

    try {
      const { detail : responsePassports } = await request<
        null,
        { detail: ResponseTicket }
      >(
        {
          requestOptions : {
            resourcePath :
              `event/${eventId}/session/0/tickets` +
              `?apikey=${apiKey}&document=${normalizedDocument}&producerId=${producerId}&passkey=${passkey}`,
          },
          type : HttpVerb.GET,
        },
        { baseUrl : Environment.API_TICKETS_URL }
      );

      const responseTickets = bffAddExtraKeysToTicketListing({
        singleDays : responsePassports.responseData,
        passports : [],
      });

      const hasMembershipTicket = verifyThisMembershipHasTicket(responseTickets, membership);

      if (!hasMembershipTicket) {
        setLoading(false);
        return false;
      }

      setShowNoTicketsContent(false);
      setPositionCarouselIfCouponOrPasskeyExists({ tickets : responseTickets });
      applyTicketRules({
        event : event!,
        couponApplied,
        updateTicketList,
        updateMembershipError : (error: MembershipRuleValue | null) => {
          if (error) {
            handleMembershipError(membership.document, error, membership);
            setLoading(false);
            return false;
          }
        },
        tickets : responseTickets,
        membership,
      });

      setPassKeyApplied(passkey);

      const cadeiraEspecialInferior = responseTickets.find(ticket => ticket.id === 1356486);
      const cadeiraSudesteInferior = responseTickets.find(ticket => ticket.id === 1357419);

      const isConmebolEvent = event?.id === 79672;
      const isSpecialChairMembership =
        membership.name === 'ESQUADRAONAFONTECADEIRAESPECIAL';
      const isSpecialChairUnavailable =
        cadeiraEspecialInferior?.salable === false &&
        cadeiraEspecialInferior.status !== 'available';
      const isSudesteChairAvailable =
        cadeiraSudesteInferior?.salable === true &&
        cadeiraSudesteInferior.status === 'available';

      const shouldShowConmebolWarning =
        isConmebolEvent &&
        isSpecialChairMembership &&
        isSpecialChairUnavailable &&
        isSudesteChairAvailable;

      if (shouldShowConmebolWarning) {
        setContentModal(<ConmebolWarningModal />);
        toggleModal({ open : true });
      }

      return true;
    } catch (error) {
      logger.error(error as string);
      setNoTicketContentKey(DictonaryKeys.ticketListError);
      setShowNoTicketsContent(true);
      handleMembershipLoadings(false);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const changeEventLoading = (active: boolean) => setLoading(active);

  /**
   * Verifica se o evento pertence à alguma categoria que altera o comportamento da aplicação como "Futebol" ou "O Globo".
   *
   * Esta função verifica se o evento fornecido contém a categoria "Futebol" ou "O Globo" e, em caso afirmativo,
   * define o estado `isFootball` como verdadeiro ou o estado `isOGlobo` como verdadeiro.
   *
   * @param event - O objeto do evento que contém as categorias do evento.
   */
  const checkIfEventCategoryChangeRules = ({ event }: { event: Event }) => {
    setIsFootball(event.categories.some(({ id }) => id === Categories.Futebol));
    setIsOGlobo(event.categories.some(({ id }) => id === Categories.OGlobo));
  };

  /**
   * Selects the specified date carousel ID.
   * Updates the state with the provided ID, setting it as the selected ID for the date carousel.
   * @param id - The ID to be set as the selected date carousel ID.
   */
  const selectIdDateCarousel = (id: number) => {
    setSelectedIdDateCarousel(id);
  };

  const selectTicketGroup = (ticket: Ticket | null) =>
    setTicketGroupSelected(ticket);

  const formatDateToWeekAndHour = (adjustedDate: Date): string => {
    const formatHour = locale === 'en-US' ? 'EEEE - hh\':\'mm a' : 'EEEE - HH\':\'mm';
    const formatDate = locale === 'en-US' ? 'MMMM dd yyyy' : 'dd MMMM yyyy';

    return format(adjustedDate, `${formatHour} - ${formatDate}`, {
      locale : supportedLocales({ lang : locale }),
    });
  };

  /**
   * Formats a time string into a specific format.
   * This function takes a time string in the format `HH:mm:ss`, parses it into a Date object,
   * and then formats it into a string with the format `HH'h'`, based on the specified locale.
   * @param {string} timeString - The time string to be formatted.
   * @returns string The formatted time string in `HH'h'` format.
   */
  const formatTime = (adjustedDate: Date): string => {
    const hourFormat = locale === 'en-US' ? 'EEEE - hh\':\'mm a' : 'EEEE - HH\':\'mm';

    return format(adjustedDate, hourFormat, {
      locale : supportedLocales({ lang : locale }),
    }).toLowerCase();
  };

  const getTimePrefix = (locale: string): string => {
    const prefixes: { [key: string]: string } = {
      'en-US' : 'at',
      'es-ES' : 'a las',
      'pt-BR' : 'às',
    };
    return prefixes[locale] || 'at';
  };

  const formatDateWithLocale = (adjustedDate: Date): string => {
    const formatHour = locale === 'en-US' ? 'hh:mm a' : 'HH:mm';
    const formatDate = locale === 'en-US' ? 'EEE, MM/dd/yy' : 'EEE, dd/MM/yy';

    const timePrefix = getTimePrefix(locale);
    const timeSufix = locale === 'en-US' ? '' : 'h';

    const formattedDate = format(adjustedDate, formatDate, {
      locale : supportedLocales({ lang : locale }),
    });

    const dayOfWeek = formattedDate.split(' ')[0].slice(0, 3);
    const capitalizedDay = dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);

    const capitalizedDate = formattedDate.replace(formattedDate.split(' ')[0], `${capitalizedDay},`);

    return `${capitalizedDate} ${timePrefix} ${format(adjustedDate, formatHour, {
      locale : supportedLocales({ lang : locale }),
    })}${timeSufix}`;
  };

  const getPassportLabel = (): string => {
    const customLabel = event?.attributes.passportLabel.value;
    if (customLabel && customLabel !== 'Passaporte') return customLabel;

    return translate(['TICKET_TEMPLATE', 'PASSPORT', 'SESSION']).toUpperCase();
  };

  const extractDate = (formattedString: string) => {
    const parts = formattedString.split(' - ');
    if (parts.length < 3) return formattedString;
    const commentedPart = parts.slice(2).join(' - ');
    return commentedPart.toUpperCase();
  };

  const formatTickets = (tickets: Ticket[]): DataCarouselProps[] =>
    formatTicketsHelper(tickets, {
      sessionDetails,
      event,
      hasSellableTicketType,
      checksIfAllTicketsAreSoldOut,
      formatDate,
      formatTime,
      formatDateToWeekAndHour,
      formatDateWithLocale,
      extractDate,
      getPassportLabel,
      selectIdDateCarousel,
    });

  const sortDataCarousel = (dataCarousel: DataCarouselProps[]) => {

    const startWithPassports = !event?.attributes.passportAtBottom.value;

    let sortedEvents = [
      ...dataCarousel.filter(curEvent => !curEvent.finished && !curEvent.isPassport),
      ...dataCarousel.filter(curEvent => curEvent.isPassport && !curEvent.finished),
      ...dataCarousel.filter(curEvent => curEvent.finished),
    ];

    if (startWithPassports) {
      sortedEvents = [
        ...dataCarousel.filter(curEvent => curEvent.isPassport && !curEvent.finished),
        ...dataCarousel.filter(curEvent => !curEvent.finished && !curEvent.isPassport),
        ...dataCarousel.filter(curEvent => curEvent.finished),
      ];
    }

    return sortedEvents;
  };

  /**
   * Generates a list of dates for the carousel based on the available tickets.
   * Formats the tickets, separates them into categories, sorts them, and sets the data carousel.
   * @param startPosition - An object with a boolean property indicating whether to set the initial position.
   */
  const generateDateCarouselList = (): void => {
    if (tickets.length === 0) return;

    // Prevent race condition: wait for session details to be loaded
    if (!sessionDetails && tickets.some(ticket => !ticket.isPassport)) return;

    const eventComboExclusiveSales = event?.attributes.comboExclusiveSales?.value ?? false;

    // Format all tickets
    let ticketsWithSessionsHandled = [...tickets];
    if (event?.attributes.sessionHidePast.value) {
      ticketsWithSessionsHandled = tickets.filter((ticketGroup) => (
        !ticketGroup.type.every((ticket) => (
          ticket.dates.every((date) => date.status === 'finished'))
        )
      ));
    }

    const formattedTickets = formatTickets(ticketsWithSessionsHandled);

    // Inject dates that have no available tickets but do have resale listings.
    // These are dates filtered out by updateTicketList (finished/soldout) but
    // recoverable via the /grouped endpoint. Fail-open: if resaleDateIds is empty, no-op.
    if (resaleDateIds.size > 0 && sessionDetails) {
      const existingDateIds = new Set(formattedTickets.map(item => item.id));
      for (const [dateId, { count : resaleCount, datetime }] of resaleDateIds) {
        if (existingDateIds.has(dateId)) continue;
        // Skip past sessions (absolute-instant compare, timezone-invariant; fail open if malformed).
        const sessionStartTimestamp = Date.parse(datetime);
        if (!Number.isNaN(sessionStartTimestamp) && sessionStartTimestamp < Date.now()) continue;
        // Use the ticket payload's full ISO datetime (with offset) — same source as the
        // normal path in formatTickets — so formatDate converts UTC → event timezone correctly.
        const adjustedDate = formatDate(datetime, event?.timezone ?? 'America/Sao_Paulo');
        formattedTickets.push({
          id : dateId,
          title : extractDate(formatDateToWeekAndHour(adjustedDate)),
          subtitle : formatTime(adjustedDate),
          sessionTitle : formatDateWithLocale(adjustedDate),
          weekAndHour : formatDateToWeekAndHour(adjustedDate),
          isPassport : false,
          days : null,
          hasTicketAvailable : false,
          hasResale : true,
          resaleCount,
          finished : false,
          status : 'soldout',
          groupsReference : [],
          definitiveSessionId : String(dateId),
          callback : () => selectIdDateCarousel(dateId),
        });
      }
    }

    // Sort non-passport items chronologically so injected resale dates land in the right position.
    // sortDataCarousel then groups by available/finished — stable sort preserves this order within groups.
    if (sessionDetails) {
      const dateIdToIso = new Map<number, string>();
      sessionDetails.date.forEach(sd => {
        if (!sd.dateTime?.date) return;
        const [d, m, y] = sd.dateTime.date.split('/');
        dateIdToIso.set(sd.id, `${y}-${m}-${d}T${sd.dateTime.time}`);
      });
      formattedTickets.sort((a, b) => {
        if (a.isPassport || b.isPassport) return 0;
        const aIso = dateIdToIso.get(a.id);
        const bIso = dateIdToIso.get(b.id);
        // Preserve existing order when either side lacks a date — don't force
        // date-less items to the front (stable sort keeps their prior position).
        if (!aIso || !bIso) return 0;
        return aIso.localeCompare(bIso);
      });
    }

    let listCarouselData = sortDataCarousel(formattedTickets);
    const hasCombo = comboList.length > 0;

    if (listCarouselData.length === 1) {
      const isPassport = listCarouselData[0].isPassport;
      const dateTime = tickets[0].type[0].dates[0].datetime;
      const adjustedDate = formatDate(dateTime, event?.timezone ?? 'America/Sao_Paulo');
      const formatHour = locale === 'en-US' ? 'hh\':\'mm a' : 'HH\':\'mm';
      const dateFormatted = locale === 'en-US' ? 'eeee - MMMM dd' : 'eeee - dd \'DE\' MMMM';
      const formattedDate = format(adjustedDate,
        `${dateFormatted} '${getTimePrefix(locale)}' ${formatHour}`, {
          locale : supportedLocales({ lang : locale }),
        });

      if (isPassport) {
        const label = getPassportLabel();
        setSingleDayDate(` - ${label}`);
      } else {
        setSingleDayDate(
          formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1)
        );
      }
    } else {
      setSingleDayDate('');
    }

    if (hasCombo) {
      const definitiveComboCarouselId = comboList.map((combo) => combo.id).join('');
      const comboCarouselData = {
        days : null,
        definitiveSessionId : definitiveComboCarouselId,
        finished : false,
        groupsReference : [],
        hasTicketAvailable : true,
        id : Number(definitiveComboCarouselId),
        isPassport : false,
        title : 'COMBO',
        weekAndHour : null,
        callback : () => selectIdDateCarousel(Number(definitiveComboCarouselId)),
      };

      listCarouselData = [comboCarouselData, ...listCarouselData].sort((cur, next) => {
        if (cur.isPassport && next.id === Number(definitiveComboCarouselId)) return -1;
        if (next.isPassport && cur.id === Number(definitiveComboCarouselId)) return 1;
        return 0;
      });
    }

    if (hasCombo) {
      listCarouselData = listCarouselData.filter((item) => {
        if (item.title === 'COMBO' || item.isPassport) return true;

        if (eventComboExclusiveSales) return false;

        const ticketsForThisSession = ticketsWithSessionsHandled.filter(
          (ticket) => item.groupsReference.includes(ticket.id)
        );

        const allTicketsHaveComboExclusive = ticketsForThisSession.every((ticket) =>
          ticket.type.every((ticketType) => ticketType.comboExclusiveSales === true)
        );

        return !allTicketsHaveComboExclusive;
      });
    }

    setDataCarousel(listCarouselData);
    const firstSelectable = listCarouselData.find(item => !item.hasResale) ?? listCarouselData[0];
    if (firstSelectable) selectIdDateCarousel(firstSelectable.id);

  };

  /**
   * Generates and sets the date carousel list.
   * This function formats all tickets into `DataCarouselProps` objects, categorizes them into passport items, single items,
   * and other items based on their availability and type, sorts the non-passport items alphabetically by title,
   * and then combines all the items into a single list which is set to the data carousel.
   * @returns void This function does not return a value.
   * @remarks
   * - If there are no tickets, the function exits early.
   * - Tickets are first formatted using the `formatTicket` function.
   * - Formatted tickets are categorized based on their type and availability.
   * - Non-passport items are sorted alphabetically by their title.
   * - The final sorted list is set to the data carousel.
   */
  async function getGetTickets({ event }: { event: Event }) {
    try {
      toggleLoader({ show : true });
      setLoading(true);
      const resourcePath = `/event/${eventId}/session/0/tickets?apikey=${Environment.API_KEY}`;

      const [{ detail }, resaleGroups] = await Promise.all([
        request<null, { detail: ResponseTicket }>(
          { requestOptions : { resourcePath }, type : HttpVerb.GET },
          { baseUrl : Environment.API_TICKETS_URL }
        ),
        Promise.race([
          request<null, { detail: { groups: { guestTypeId: number; count: number }[] } }>(
            {
              requestOptions : {
                resourcePath : `/resale/event/${eventId}/grouped?apikey=${Environment.API_KEY}`,
              },
              type : HttpVerb.GET,
            },
            { baseUrl : Environment.API_GATEWAY_URL }
          ).then(r => r?.detail?.groups ?? []),
          new Promise<{ guestTypeId: number; count: number }[]>(resolve =>
            setTimeout(() => resolve([]), 5000)
          ),
        ]).catch((err) => { console.error('[resale/grouped]', err); return []; }),
      ]);

      // Build typeId → date map from all raw ticket groups (before salable filter)
      // so we can match resale guestTypeId to a carousel date. Carry the full ISO
      // datetime (with offset) so injected resale dates format with the correct timezone.
      const typeIdToDate = new Map<number, { dateId: number; datetime: string }>();
      for (const group of detail.responseData) {
        for (const type of group.type) {
          const date = type.dates[0];
          if (date?.id !== undefined && date.datetime) {
            typeIdToDate.set(type.id, { dateId : date.id, datetime : date.datetime });
          }
        }
      }

      const dateIds = new Map<number, { count: number; datetime: string }>();
      for (const g of resaleGroups) {
        const date = typeIdToDate.get(g.guestTypeId);
        if (!date) continue;
        const existing = dateIds.get(date.dateId);
        dateIds.set(date.dateId, {
          count : (existing?.count ?? 0) + (g.count ?? 0),
          datetime : date.datetime,
        });
      }

      setResaleDateIds(dateIds);

      const passkey = passKeyApplied || '';
      const singleDays = detail.responseData;
      const passports = await getGetPassports();

      const comboEnabled = event?.attributes?.comboEnabled?.value;
      if (comboEnabled) await getCombos(passkey);

      // BFF - Merging responses and adding necessary keys for the flow
      const tickets = bffAddExtraKeysToTicketListing({
        singleDays : singleDays.filter((group) => group.salable).map((group) => {
          group.type.forEach((type) => {
            type.idCarouselRef = group.id;
          });
          return group;
        }),
        passports : passports.filter((group) => group.salable).map((group) => {
          group.type.forEach((type) => {
            type.idCarouselRef = group.id;
          });
          return group;
        }),
      });
      applyTicketRules({
        event,
        couponApplied,
        updateTicketList,
        tickets,
      });
    } catch (error) {
      logger.error(error as string);
    } finally {
      setLoading(false);
      toggleLoader({ show : false });
      setEvent(event);
      setProducerId(event.producerId);
      setCurrencyFormat(event.accepted_currencies);
    }
  }

  async function getCombos(passkey = '') {
    try {
      const token = getCookieValue({ cookieKey : CookiesKeys.TOKEN });
      const { responseData } = await request<null, ResponseComboList>(
        {
          requestOptions : {
            resourcePath : (
              `/event/${eventId}/combo/available?apikey=${Environment.API_KEY}&usertoken=${token}&passkey=${passkey}`
            ),
          },
          type : HttpVerb.GET,
        },
        { baseUrl : Environment.API_URL, needToken : true }
      );

      setComboList(responseData.data || []);
    } catch (error) {
      logger.error(error as string);
      return [];
    } finally {
      setLoading(false);
    }
  }

  /**
   * Fetches passport tickets for an event.
   * This asynchronous function retrieves passport tickets from the API for a specified event.
   * If a `passKey` is provided, it is included in the API request. The function handles the API
   * response, returning the ticket details or an empty array in case of an error.
   * @param {Object} params - The parameters for the function.
   * @param {string | null} params.passKey - The optional pass key to include in the API request.
   * @returns Promise<Ticket[]> A promise that resolves to an array of tickets.
   * @throws {Error} Will log an error message if the API request fails.
   * @remarks
   * - The function uses `ApiService` to make the API request.
   * - The `resourcePath` is constructed based on the presence of `passKey`.
   * - If an error occurs during the API request, it logs the error and returns an empty array.
   * - The loading state is set to `false` after the request completes, regardless of success or failure.
   */
  async function getGetPassports() {
    try {
      const { detail } = await request<null, { detail: ResponseTicket }>(
        {
          requestOptions : {
            resourcePath : `/event/${eventId}/session/passports/tickets?apikey=${Environment.API_KEY}`,
          },
          type : HttpVerb.GET,
        },
        { baseUrl : Environment.API_TICKETS_URL }
      );

      return detail.responseData;
    } catch (error) {
      logger.error(error as string);
      return [];
    }
  }

  /**
   * Fetches event details.
   * This asynchronous function retrieves event details from the API for a specified event.
   * After fetching the event details, it calls `getGetTickets` with the event data.
   * @returns Promise<void> A promise that resolves when the event details are fetched and processed.
   * @throws {Error} Will log an error message if the API request fails.
   * @remarks
   * - The function uses `ApiService` to make the API request.
   * - The `resourcePath` is constructed to fetch public event details based on `eventId`.
   * - If an error occurs during the API request, it logs the error.
   * - The loading state is set to `false` after the request completes, regardless of success or failure.
   */
  async function getEventDetails() {
    // Skip if eventId is not defined (e.g., in resale pages)
    if (!eventId) {
      logger.warn('[EventContext] Skipping getEventDetails: eventId is undefined');
      return;
    }

    try {
      const { data : dataEvent } = await request<null, { data: Event }>(
        {
          requestOptions : { resourcePath : `/public/${eventId}` },
          type : HttpVerb.GET,
        },
        { baseUrl : Environment.API_EVENT_URL }
      );

      const { value : salesEnabled } = dataEvent.attributes.saleEnabled;

      if (!salesEnabled) {
        setLoading(false);
        toggleLoader({ show : false });
        setNoTicketContentKey(DictonaryKeys.salesIsNotEnable);
        setShowNoTicketsContent(true);
        setEvent(dataEvent);

        return false;
      }

      setShowQuantityInStock(dataEvent.attributes.showQuantityInStock.value);

      /**
       * Checks if the event data contains a start image and sets the start image accordingly.
       * - If `dataEvent.attributes.startImage` exists:
       *   - Extracts the keys of the start image value.
       *   - Checks if the keys include 'large'.
       *   - If there are any keys, sets the start image to 'large' if it exists, otherwise to the first key.
      */
      if (dataEvent.attributes.startImage.value) {
        type StartImageProps = Array<
          keyof typeof dataEvent.attributes.startImage.value
        >;
        const keyImagesRef = Object.keys(
          dataEvent.attributes.startImage.value
        ) as StartImageProps;
        if (keyImagesRef.length > 0) {
          setStartImage({
            large : dataEvent.attributes.startImage.value.large,
            small : dataEvent.attributes.startImage.value.medium,
          });
        }
      }

      setSessions(dataEvent.sessions);
      getGetTickets({ event : dataEvent });
      checkIfEventCategoryChangeRules({ event : dataEvent });
      return true;
    } catch (error) {
      logger.error(error as string);

      setNoTicketContentKey(DictonaryKeys.eventNotFound);
      setShowNoTicketsContent(true);

      setLoading(false);
      toggleLoader({ show : false });
    }
  }

  async function getSessionDetails() {
    try {
      const data = await request<null, responseEventType>(
        {
          requestOptions : { resourcePath : `/event/${eventId}?apikey=${Environment.API_KEY}` },
          type : HttpVerb.GET,
        },
        { baseUrl : Environment.API_URL }
      );

      setSessionDetails(data.responseData);

    } catch (error) {
      logger.error(error as string);
      setLoading(false);
      toggleLoader({ show : false });
    }
  }
  /**
   * Applies a coupon for the current event.
   * This asynchronous function validates and applies a coupon by making an API request.
   * It updates the coupon applied state with the response data.
   * @param {Object} params - The parameters for the function.
   * @param {string} params.coupon - The coupon code to be applied.
   * @returns Promise<void> A promise that resolves when the coupon is applied and processed.
   * @throws {Error} Will log an error message if the API request fails.
   * @remarks
   * - The function uses `ApiService` to make the API request.
   * - The `resourcePath` is constructed to validate the coupon based on the coupon code and `eventId`.
   * - The loader is toggled on before the API request and off after the request completes.
   * - If an error occurs during the API request, it logs the error.
   */
  async function applyCoupon({
    coupon, modalIsOpen, setContentModal, toggleModal }: {
      coupon: string,
      modalIsOpen: boolean;
      setContentModal: (content: React.ReactNode) => void,
      toggleModal: ({ open }: { open: boolean }) => void
    }): Promise<boolean> {
    try {
      toggleLoader({ show : true });
      const resourcePath: string = `/coupons/validate/${coupon}/${eventId}?apikey=${Environment.API_KEY}`;
      const { detail } = await request<null, CouponObject>(
        {
          requestOptions : { resourcePath },
          type : HttpVerb.GET
        },
        { baseUrl : Environment.API_BACKSTAGE_URL }
      );

      if (!detail.isValid) return false;

      const sessionResourcePath =
        `/event/${eventId}/session/0/tickets?apikey=${Environment.API_KEY}&coupon=${coupon}`;
      const responseSession = await request<null, ResponseTicket>(
        {
          requestOptions : { resourcePath : sessionResourcePath },
          type : HttpVerb.GET,
        },
        { baseUrl : Environment.API_URL }
      );

      const passportSession = await request<null, ResponseTicket>(
        {
          requestOptions :
          {
            resourcePath :
              `/event/${eventId}/session/passports/tickets?apikey=${Environment.API_KEY}&coupon=${coupon}`
          },
          type : HttpVerb.GET,
        },
        { baseUrl : Environment.API_URL }
      );

      const singleDays = responseSession.responseData.filter(group => group.salable);
      const passports = passportSession.responseData.filter(group => group.salable);

      setCouponApplied(detail);

      // BFF - Merging responses and adding necessary keys for the flow
      const updatedTickets = bffAddExtraKeysToTicketListing({ singleDays, passports });
      applyTicketRules({
        event : event!,
        couponApplied,
        updateTicketList,
        tickets : updatedTickets,
      });
      return true;
    } catch (error) {
      logger.error(error as string);
      setPassKeyApplied(null);

      modalIsOpen === false && setContentModal(
        <ErrorMessage error={{ code : 'COUPON_ALREADY_USED_OR_INVALID' }} />
      );
      modalIsOpen === false && toggleModal({ open : true });
      return false;
    } finally {
      toggleLoader({ show : false });
    }
  }

  async function applyGloboSignature({
    globoSignature,
    modalIsOpen,
    setContentModal,
    toggleModal
  }: {
    globoSignature: string,
    modalIsOpen: boolean;
    setContentModal: (content: React.ReactNode) => void,
    toggleModal: ({ open }: { open: boolean }) => void
  }): Promise<boolean> {
    try {
      toggleLoader({ show : true });
      const resourcePath: string = `/event/${eventId}/globo-signature/${globoSignature}?apikey=${Environment.API_KEY}`;

      const { responseData } = await request<
        null,
        { responseData: { validated: boolean } }
      >(
        {
          requestOptions : { resourcePath },
          type : HttpVerb.GET,
        },
        { baseUrl : Environment.API_URL }
      );

      setSignatureOGloboApplied(responseData.validated ? (
        globoSignature
      ) : null);

      if (responseData.validated) applyCoupon({
        coupon : 'CPFGLOBO',
        modalIsOpen,
        setContentModal,
        toggleModal
      });

      return responseData.validated;
    } catch (error) {
      logger.error(error as string);
      setSignatureOGloboApplied(null);

      modalIsOpen === false && setContentModal(
        <ErrorMessage error={{ code : 'GLOBO_SIGNATURE_INVALID' }} />
      );
      modalIsOpen === false && toggleModal({ open : true });
      return false;
    } finally {
      toggleLoader({ show : false });
    }
  }

  /**
   * Applies a passkey for the current event.
   * This asynchronous function validates and applies a passkey by making an API request.
   * It merges the response data with passport tickets, adds necessary keys, and applies ticket selection rules.
   * @param {Object} params - The parameters for the function.
   * @param {string} params.passkey - The passkey to be applied.
   * @returns Promise<void> A promise that resolves when the passkey is applied and processed.
   * @throws {Error} Will log an error message if the API request fails.
   * @remarks
   * - The function uses `ApiService` to make the API request.
   * - The `resourcePath` is constructed to validate the passkey based on the passkey and `eventId`.
   * - The loader is toggled on before the API request and off after the request completes.
   * - If an error occurs during the API request, it logs the error.
   * - The function merges the single day tickets with passport tickets, adds necessary keys, and applies ticket selection rules.
   * - The function sets various states after the request completes, such as `event`, `producerId`, and `currencyFormat`.
   */
  async function applyPassKey({
    passkey,
    modalIsOpen,
    setContentModal,
    toggleModal
  }: {
    passkey: string;
    modalIsOpen: boolean;
    setContentModal: (content: React.ReactNode) => void;
    toggleModal: ({ open }: { open: boolean }) => void
  }): Promise<boolean> {
    try {
      toggleLoader({ show : true });

      let resourcePath: string = `/event/${eventId}/session/0/tickets?apikey=${Environment.API_KEY}&passkey=${passkey}`;
      const { detail : responseSingleDays } = await request<
        null,
        { detail: ResponseTicket }
      >(
        {
          requestOptions : { resourcePath },
          type : HttpVerb.GET,
        },
        { baseUrl : Environment.API_TICKETS_URL }
      );

      resourcePath = `/event/${eventId}/session/passports/tickets?apikey=${Environment.API_KEY}&passkey=${passkey}`;

      const { detail : responsePassports } = await request<
        null,
        { detail: ResponseTicket }
      >(
        {
          requestOptions : { resourcePath },
          type : HttpVerb.GET,
        },
        { baseUrl : Environment.API_TICKETS_URL }
      );

      const singleDays = responseSingleDays.responseData.filter(
        (group) => group.salable
      );
      const passports = responsePassports.responseData.filter(
        (group) => group.salable
      );

      const hasPasskey = [...singleDays, ...passports].some((passport) =>
        passport.type.some((type) => type.hidden)
      );

      if (!hasPasskey) {
        setPassKeyApplied(null);
        !modalIsOpen && setContentModal(
          <ErrorMessage error={{ code : 'CODE_ALREADY_USED_OR_INVALID' }} />
        );
        !modalIsOpen && toggleModal({ open : true });
        return false;
      }

      if (!event?.attributes.saleEnabled.value) {
        setPassKeyApplied(null);
        return false;
      }

      const comboEnabled = event?.attributes?.comboEnabled?.value;
      if (comboEnabled) await getCombos(passkey);
      setPassKeyApplied(passkey);
      setShowNoTicketsContent(false);

      // BFF - Merging responses and adding necessary keys for the flow
      const updatedTickets = bffAddExtraKeysToTicketListing({ singleDays, passports });
      setPositionCarouselIfCouponOrPasskeyExists({ tickets });
      applyTicketRules({
        event : event!,
        couponApplied,
        updateTicketList,
        tickets : updatedTickets,
      });
      return true;
    } catch (error) {
      logger.error(error as string);
      return false;
    } finally {
      setLoading(false);
      toggleLoader({ show : false });
      setEvent(event);
      event && setProducerId(event.producerId);
      event && setCurrencyFormat(event.accepted_currencies);
    }
  }

  /**
   * Removes the applied passkey and refreshes the tickets for the specified event.
   * Sets the passkey to null and then retrieves the updated tickets for the event.
   */
  async function removePassKeyApplied() {
    setPassKeyApplied(null);
    await getGetTickets({ event : event! });
    changeMembershipApplied(null);
    if (dataCarousel && dataCarousel.length > 0) {
      const firstSelectable = dataCarousel.find(item => !item.hasResale) ?? dataCarousel[0];
      selectIdDateCarousel(firstSelectable.id);
    }
  }

  /**
   * Removes the applied coupon and refreshes the tickets for the specified event.
   * Sets the coupon to null and then retrieves the updated tickets for the event.
   */
  async function removeCouponApplied() {
    setCouponApplied(null);
    await getGetTickets({ event : event! });
    const firstSelectable = dataCarousel.find(item => !item.hasResale) ?? dataCarousel[0];
    if (!firstSelectable) return;
    selectIdDateCarousel(firstSelectable.id);
  }

  async function removeSignatureOGloboApplied() {
    setSignatureOGloboApplied(null);
    removeCouponApplied();
  }

  /**
   * Creates a deep clone of the given object.
   * This function takes an object and returns a deep copy of it by serializing and deserializing
   * the object using JSON. This ensures that all nested objects and arrays are also cloned.
   * @template T - The type of the input object.
   * @param The object to be cloned.
   * @returns A deep clone of the input object.
   */
  function deepClone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }

  /**
   * Applies rules after adding an item to the cart.
   * Updates the ticket quantities in the cart, clones the tickets, and applies the selection rules.
   * @param cartItems - The array of ticket items in the cart.
   */
  function applyRulesAfterAddItemInCart({
    cartItems,
  }: {
    cartItems: Ticket[];
  }) {
    const newTickets = cartItems.map((ticket) => {
      const ticketId = ticket.id;

      const toUpdate = tickets.find((item) => item.id === ticketId);

      if (!toUpdate) return ticket;

      const updatedTypes = toUpdate.type.map((type) => {
        const quantityInCart =
          ticket.type.find((cartItem) => cartItem.id === type.id)
            ?.quantityInCart ?? 0;
        return {
          ...type,
          quantityInCart,
        };
      });

      return { ...toUpdate, type : updatedTypes };
    });

    const copyTickets = tickets.map((ticket) => deepClone(ticket));

    copyTickets.forEach((ticket) => {
      const currentTicketId = ticket.id;

      newTickets.forEach((newTicket) => {
        const newTicketId = newTicket.id;

        if (currentTicketId !== newTicketId) return;

        ticket.type.forEach((type, index) => {
          const currentTypeId = type.id;
          const newType = newTicket.type.find(
            (newType) => newType.id === currentTypeId
          );

          if (!newType) return;

          ticket.type[index] = {
            ...ticket.type[index],
            quantityInCart : newType.quantityInCart,
            accumulatedValueInCart : newType.accumulatedValueInCart,
          };
        });
      });
    });

    applyTicketRules({
      event : event!,
      couponApplied,
      updateTicketList,
      tickets : copyTickets,
    });
  }

  /**
   * Sets the position of the carousel if a ticket with a passkey or coupon exists.
   * Finds the first ticket with a hidden type (passkey) or a type with a tagType (coupon)
   * and sets the carousel position to the ID of that ticket.
   * @param tickets - The array of tickets to check.
   * @returns void
   */
  function setPositionCarouselIfCouponOrPasskeyExists({
    tickets,
  }: {
    tickets: Ticket[];
  }) {
    const ticketWithPasskey = tickets.find((ticket) =>
      ticket.type.some((type) => type.hidden)
    );
    const ticketWithCoupon = tickets.find((ticket) =>
      ticket.type.some((type) => type.tagType)
    );

    if (ticketWithPasskey) {
      return selectIdDateCarousel(ticketWithPasskey.id);
    }

    if (ticketWithCoupon) {
      return selectIdDateCarousel(ticketWithCoupon.id);
    }
  }

  const checksIfAnyGroupIsRequiredRF = (tickets: Ticket[]) => {
    return tickets.some((ticket) => ticket.transferMandatoryFaceRecognition);
  };

  /**
   * useEffect hook to handle side effects when the tickets array or `isFootball` changes.
   * This hook runs whenever the `tickets` array or the `isFootball` state changes and performs the following actions:
   * - If there are tickets available (`tickets.length > 0`), it:
   *   - Generates a date carousel list and starts from the first position.
   *   - Checks if all tickets are unavailable and updates the state accordingly.
   * - If there is only one ticket available (`tickets.length === 1`) or the event is football (`isFootball`), it formats the date for the event.
   * @param tickets - The array of ticket objects.
   * @param isFootball - The boolean state indicating if the event is football.
   */
  useEffect(() => {
    if (event && noTicketContentKey !== DictonaryKeys.salesIsNotEnable) {
      const soldOutTickets = checkIfAllTicketsAreUnavailable({ tickets, event });
      setAllTicketsUnavailable(soldOutTickets);

      if (soldOutTickets) {
        setNoTicketContentKey(isFootball ? DictonaryKeys.football : DictonaryKeys.soldOut);
        setShowNoTicketsContent(true);
      }
    }

    if (tickets.length > 0) {
      generateDateCarouselList();

      if ((tickets.length === 1 || isFootball) && resaleDateIds.size === 0) {
        const isPassport = tickets[0].isPassport;
        const dateTime = tickets[0].type[0].dates[0].datetime;

        const adjustedDate = formatDate(dateTime, event?.timezone ?? 'America/Sao_Paulo');
        const formatHour = locale === 'en-US' ? 'hh\':\'mm a' : 'HH\':\'mm';
        const dateFormatted = locale === 'en-US' ? 'eeee - MMMM dd' : 'eeee - dd \'DE\' MMMM';
        const formattedDate = format(adjustedDate,
          `${dateFormatted} '${getTimePrefix(locale)}' ${formatHour}`, {
            locale : supportedLocales({ lang : locale }),
          });

        if (isPassport) {
          const label = getPassportLabel();
          setSingleDayDate(` - ${label}`);
        } else {
          setSingleDayDate(
            formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1)
          );
        }
      }
    }

  }, [tickets, isFootball, locale, event, sessionDetails, resaleDateIds]);

  /**
   * useEffect hook to handle side effects when the couponApplied state changes.
   * This hook runs whenever the `couponApplied` state changes and performs the following actions:
   * - If a coupon is applied and an event exists, it applies the coupon rules to the tickets.
   * - Updates the position of the carousel if a ticket with a coupon or passkey exists.
   * - Updates the state with the modified tickets.
   * @param couponApplied - The state indicating if a coupon is applied.
   * @param event - The current event object.
   * @param tickets - The array of ticket objects.
   */
  useEffect(() => {
    if (!couponApplied || !event) return;

    GoogleAnalytics.trackPromotionView(EventTrackLabel.VIEW_PROMOTION, {
      eventId : Number(eventId),
      eventTitle : event.title,
      couponInput : couponApplied.coupon.name,
    });

    const updatedTicketWithCoupon = applyCouponRules({
      tickets,
      couponApplied,
    });

    if (dataCarousel.length > 1) {
      setPositionCarouselIfCouponOrPasskeyExists({
        tickets : updatedTicketWithCoupon,
      });
    }

    setTickets(updatedTicketWithCoupon);
  }, [couponApplied]);

  /**
   * useEffect hook to update the passportsDays state when the selectedIdDateCarousel changes.
   * This hook runs whenever the `selectedIdDateCarousel` changes and performs the following actions:
   * - Finds the item in `dataCarousel` with an ID matching `selectedIdDateCarousel`.
   * - Sets the `passportsDays` state to the `days` property of the found item.
   * - If no matching item is found, sets `passportsDays` to null.
   * @param selectedIdDateCarousel - The currently selected date carousel ID.
   */
  useEffect(() => {
    setPassportsDays(
      dataCarousel.find((item) => item.id === selectedIdDateCarousel)?.days ??
      null
    );
  }, [selectedIdDateCarousel]);

  useEffect(() => {
    generateDateCarouselList();
  }, [locale, sessionDetails]);

  useEffect(() => {
    (async () => {
      const salesEnabled = await getEventDetails();
      if (salesEnabled) await getSessionDetails();
    })();
  }, []);

  useEffect(() => {
    // Only enable the terms modal when the event carries actual content.
    // An event with empty terms (e.g. '{"title":"","description":""}') is a
    // truthy string but has nothing to show — it must not open the modal.
    setHasTermModal(hasTermModalContent(event?.attributes?.terms?.value));
  }, [event]);

  useEffect(() => {
    if (!event?.producerId) {
      setIsBotafogo(false);
      return;
    }

    const ignoreProducers = [9231081, 8772447];
    const producerId = event.producerId;

    const isBotafogo = Producers.Botafogo.includes(producerId) &&
                       !ignoreProducers.includes(producerId);

    setIsBotafogo(isBotafogo);
  }, [event]);

  useEffect(() => {
    if (isFootball && isBotafogo && user && eventId && producerId) {
      getDependentList();
    }
  }, [user, eventId, isFootball, isBotafogo, authenticatedUser]);

  useEffect(() => {
    const shouldFetch = shouldFetchFacialRecognitionUrl({
      event,
      groupHasFaceRecognition : anyGroupHasFaceRecognition(tickets),
    });

    if (eventId && shouldFetch) {
      getFacialRecognitionUrl({ eventId : Number(eventId), producerId });
    }
  }, [eventId, event, tickets, authenticatedUser, producerId]);

  useEffect(() => {
    if (!tickets) return;
    setSomeGroupRequiresRF(checksIfAnyGroupIsRequiredRF(tickets));
  }, [tickets]);

  useEffect(() => {
    // Capture the fbclid from the URL and store it in a cookie (only once on mount)
    captureFbclid();
  }, []);

  // Track Meta Conversions API PageView automatically
  useMetaConversions();

  useEffect(() => {

    // Verifica e inicializa o Facebook Pixel
    if (event?.attributes?.trackingTags?.value?.facebook?.id) {
      const facebookPixelId = event.attributes.trackingTags.value.facebook.id;

      if (facebookPixelId) {
        logger.info(`Facebook Pixel ID: ${facebookPixelId}`);

        // Inicializa o Pixel
        FacebookPixel.init(facebookPixelId);

        // Dispara o evento de visualização de página
        FacebookPixel.trackPageView();
      } else {
        logger.error('Facebook Pixel ID is missing or undefined');
      }
    }

    // Verifica e inicializa o Google Analytics
    if (event?.attributes?.trackingTags?.value?.googleAnalytics?.id) {
      const googleAnalyticsId = event.attributes.trackingTags.value.googleAnalytics.id;

      if (googleAnalyticsId) {
        logger.info(`Google Analytics ID: ${googleAnalyticsId}`);

        // Inicializa o GA
        GoogleAnalytics.init(googleAnalyticsId);

        // Dispara o evento de visualização de página
        GoogleAnalytics.trackPageView(window.location.pathname);
      } else {
        logger.error('Google Analytics ID is missing or undefined');
      }
    }
  }, [event]);

  const shared = {
    comboList,
    ticketGroupSelected,
    event,
    loading,
    tickets,
    sessions,
    producerId,
    startImage,
    isFootball,
    isOGlobo,
    signatureOGloboApplied,
    errorsLimit,
    dataCarousel,
    singleDayDate,
    passportsDays,
    couponApplied,
    currencyFormat,
    passKeyApplied,
    listOfDependents,
    selectedIdDateCarousel,
    setSelectedIdDateCarousel,
    allTicketsUnavailable,
    someGroupRequiresRF,
    showQuantityInStock,
    ticketsForThisEventInMyWallet,
    handleSetErrorsInSummary,
    getTicketsInMyWallet,
    applyCoupon,
    applyPassKey,
    isBotafogo,
    hasTermModal,
    openModalBotafogo,
    openTermModal,
    updateTicketList,
    removeCouponApplied,
    updateDependentList,
    selectIdDateCarousel,
    removePassKeyApplied,
    applyRulesAfterAddItemInCart,
    selectTicketGroup,
    getGetTickets,
    applyGloboSignature,
    removeSignatureOGloboApplied,
    changeEventLoading,
    updateTicketsWithMembership,
    noTicketContentKey,
    showNoTicketsContent,
  };

  return (
    <EventContext.Provider value={shared}>{children}</EventContext.Provider>
  );
}

EventProvider.displayName = 'EventProvider';
export const useEventContext = (): EventContextProps =>
  useContext(EventContext);

export { EventContext, EventProvider };
